import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation, useParams } from "react-router-dom"
import queryString from "query-string"
import Alert from "@material-ui/lab/Alert"
import AlertTitle from "@material-ui/lab/AlertTitle"
import { useIntl } from "react-intl"
import documentSlice from "./documentSlice"
import userFormSlice from "../../api/userFormSlice"
import CircularProgressBox from "../../component/CircularProgressBox"
import SigningCeremonyStarter from "./SigningCeremonyStarter"
import SigningCeremonyTemplate from "./SigningCeremonyTemplate"
import intlMessages from "./signingCeremonyIntl"
import useSigningStyle from "./signingCeremonyStyle"
import { actions as notifier } from "../../component/notifier"
import { CancelToken } from "../../api/httpClient"

const { actions } = documentSlice
const { actions: forms } = userFormSlice

// All managed events from callback recipient view are listed here
// returnUrl: https://developers.docusign.com/docs/esign-rest-api/reference/envelopes/envelopeviews/createrecipient/
/**
 * The recipient completed viewing an envelope that is in a read-only/terminal state, such as completed, declined, or voided.
 * @param event
 * @returns {*|boolean}
 */
function isViewingEvent(event) {
  return event && event === "viewing_complete"
}

/**
 * Recipient canceled the signing operation, possibly by using the Finish Later option.
 * @param event
 * @returns {*|boolean}
 */
function isCancelEvent(event) {
  return event && event === "cancel"
}

/**
 * The recipient completed the signing ceremony.
 * @param event
 * @returns {*|boolean}
 */
function isSigningCompleteEvent(event) {
  return event && event === "signing_complete"
}

/**
 * Recipient declined to sign.
 * @param event
 * @returns {*|boolean}
 */
function isDeclineEvent(event) {
  return event && event === "decline"
}

/**
 * The Time To Live token for the envelope has expired. After being successfully invoked, these tokens expire after 5 minutes or if the envelope is voided.
 * @param event
 * @returns {*|boolean}
 */
function isTTLExpiredEvent(event) {
  return event && event === "ttl_expired"
}

/**
 * The session timed out. An account can control this timeout by using the Signer Session Timeout option.
 * @param event
 * @returns {*|boolean}
 */
function isSessionTimeoutEvent(event) {
  return event && event === "session_timeout"
}

/**
 * Recipient used incorrect access code.
 * @param event
 * @returns {*|boolean}
 */
function isAccessCodeFailedEvent(event) {
  return event && event === "access_code_failed"
}

/**
 * Recipient failed an ID check.
 * @param event
 * @returns {*|boolean}
 */
function isIDCheckedFailedEvent(event) {
  return event && event === "id_check_failed"
}

/**
 * A system error occurred during the signing process.
 * @param event
 * @returns {*|boolean}
 */
function isException(event) {
  return event && event === "exception"
}

export default function SigningCeremonyPage() {
  const { id } = useParams()
  const history = useHistory()
  const location = useLocation()
  const { formatMessage: format } = useIntl()

  const classes = useSigningStyle()
  const signDocument = useSelector((state) => state.documents.signDocument)
  const dispatch = useDispatch()
  const { event } = queryString.parse(location.search)

  useEffect(() => {
    const { token, cancel } = CancelToken.source()
    dispatch(actions.fetchSignDocument(id, token))
    return () => cancel()
  }, [dispatch, id])

  useEffect(() => {
    // it means it's a redirection from DocuSign
    if (signDocument && isViewingEvent(event)) {
      history.push("/documents")
    }

    if (signDocument && isCancelEvent(event)) {
      history.push("/documents")
      dispatch(
        notifier.enqueueMessage(intlMessages.callbackCanceledEventInfo, {
          intlOptions: { formName: signDocument.name },
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          }
        })
      )
    }

    const { token, cancel } = CancelToken.source()
    if (
      signDocument &&
      isSigningCompleteEvent(event) &&
      signDocument.workflowStatus === 3
    ) {
      // update form when callback from docusin
      // do it only once if user decide to go back or refresh page...
      dispatch(forms.sign(id, token))
    }
    return () => cancel()
  }, [history, event, dispatch, id, signDocument])

  if (!signDocument || isCancelEvent(event)) {
    return <CircularProgressBox height="80vh" />
  }

  if (isDeclineEvent(event)) {
    const paragraphProps = {
      messages: {
        title: intlMessages.declineTitle,
        mainInfo: intlMessages.declineMainInfo,
        subInfo: intlMessages.declineSubInfo,
        button: intlMessages.return
      },
      formName: signDocument.name
    }
    return <SigningCeremonyTemplate {...paragraphProps} />
  }

  // it means it's a redirection from DocuSign
  if (isSigningCompleteEvent(event)) {
    const paragraphProps = {
      messages: {
        title: intlMessages.completeTitle,
        mainInfo: intlMessages.completeMainInfo,
        subInfo: intlMessages.completeSubInfo,
        button: intlMessages.finish
      },
      formName: signDocument.name
    }
    return <SigningCeremonyTemplate {...paragraphProps} />
  }

  const children = []
  if (isTTLExpiredEvent(event) || isSessionTimeoutEvent(event)) {
    children.push(
      <Alert severity="warning" className={classes.withMargin}>
        <AlertTitle>
          {format(intlMessages.callbackExpiredEventTitle)}
        </AlertTitle>
        {format(intlMessages.callbackExpiredEventInfo)}
      </Alert>
    )
  }

  if (isAccessCodeFailedEvent(event) || isIDCheckedFailedEvent(event)) {
    children.push(
      <Alert severity="error" className={classes.withMargin}>
        <AlertTitle>{format(intlMessages.callbackAccessEventTitle)}</AlertTitle>
        {format(intlMessages.callbackAccessEventInfo)}
      </Alert>
    )
  }

  if (isException(event)) {
    children.push(
      <Alert severity="error" className={classes.withMargin}>
        <AlertTitle>
          {format(intlMessages.callbackExceptionEventTitle)}
        </AlertTitle>
        {format(intlMessages.callbackExceptionEventInfo)}
      </Alert>
    )
  }

  return (
    <SigningCeremonyStarter formName={signDocument.name} id={id}>
      {children}
    </SigningCeremonyStarter>
  )
}
